import model from "@js/model.js";
import { addQueryStringValues, deepMerge, generateUniqueId, isObject } from "@helpers/utils.js"
import EventDispatcher from "@helpers/event-dispatcher.js";
import { parseXML } from "@helpers/dom.js";

export default class WebService extends EventDispatcher {
	static RETRY_COUNT = 5;
	static RETRY_HTTP_STATUS_CODES = [0, /*500,*/ 504];
	static PREVENT_RETRY_UPON_ACTION_TYPE = ['keepalive'];

	constructor(url) {
		super();
		this.url = url;
		this.pool = [];
		this.postponePool = [];
		model.globalRequests = 0;

		document.addEventListener('visibilitychange', () => {
			if(document.hidden) return;
			for (var i = 0, j = this.postponePool.length; i < j; i++) this.#sendHTTPReq(this.postponePool[i]);
			this.postponePool.splice(0, this.postponePool.length);
		}, false);
	}

	/**
	 * @param  {Object} xml - Includes XML data in String format and the appropriate URL suffix. 
	 * @param  {String} suffix
	 * @param  {String} url
	 * @param  {String} data
	 * @param  {Boolean} global
	 * @param  {Function} success
	 * @param  {Boolean} showClientFailure
	 */
	send(params) {
		params = deepMerge({
			type: 'POST',
			url: this.url,
			global: true,
			showClientFailure: true,
			dataType: 'text',
			contentType: 'text/xml',
			dispatchResponseReceived: true,
			requestBody: {},
			processData: true,
		}, params || {});

		if(typeof params.url !== 'string') return;
		
		var data, suffix, actionType, hasAction = false;
		if(isObject(params.xml)){
			data = params.xml.data;
			suffix = params.xml.suffix;
			actionType = params.xml.actionType;
			hasAction = true;
		}
		else if(params.data) data = params.data;

		if(typeof params.suffix === 'string') suffix = params.suffix;

		if(suffix) params.url += suffix;

		// if(model.wheelDisplayed) params.global = false;

		var options = {
			uniqueId: generateUniqueId(),
			type: params.type,
			url: params.url,
			data: data,
			cache: params.cache,
			contentType: params.contentType,
			dataType: params.dataType,
			global: params.global,
			success: (data, status, xhr) => {
				if(params.global) model.globalRequests--;
				model.dispatchEvent('HidePreloader');

				if(params.dispatchResponseReceived){
					this.dispatchEvent("ResponseReceived", {
						value: data,
						showClientFailure: params.showClientFailure,
						hasAction: hasAction,
						success: params.success,
						global: params.global,
					});
				} 
				else if(typeof params.success === 'function') params.success.call(this, data, status, xhr);

				if(model.networkError){
					this.#haltRetryInterval();
					this.#sendFirstReqFromPool();
					this.retrying = false;
					
					this.pool.shift();
					
					for(var i = 0, j = this.pool.length; i < j; i++)
						this.#sendHTTPReq(this.pool[i]);
					
					while(this.pool.length > 0)
						this.pool.pop();

					model.networkError = false;

					model.dispatchEvent('NetworkError', {
						msg: model.getStr('NetworkConnResolved'),
						status: 'success',
					});
				}
			},
			error: (xhr, textStatus, errorThrown) => {
				if(params.global) model.globalRequests--;
				model.dispatchEvent('HidePreloader');

				if(params.ignoreNetworkError) {
					if(typeof params.onIgnoredNetworkError === 'function') params.onIgnoredNetworkError();
					return;
				}

				if(typeof params.error === 'function') params.error.call(this, xhr, textStatus, errorThrown);

				if(xhr.status == 0 && typeof actionType === 'string' && this.constructor.PREVENT_RETRY_UPON_ACTION_TYPE.includes(actionType.toLowerCase())) return;

				if(!this.constructor.RETRY_HTTP_STATUS_CODES.includes(xhr.status)){
					model.dispatchEvent('HandleServerError', {
						xhr: xhr,
						textStatus: textStatus,
						errorThrown: errorThrown,
					});				
					return;
				}

				model.networkError = true;
				var reqAlreadyInPool = false;

				for(var i = 0, j = this.pool.length; i < j; i++){
					var reqInPool = this.pool[i];

					if(options.uniqueId == reqInPool.uniqueId){
						reqAlreadyInPool = true;
						break;
					}
				}

				!reqAlreadyInPool && this.pool.push(options);				
				this.#startRequestRetries();
			},
		};

		options.headers = {};

		if(hasAction){
			if(model.headerLBToken && model.uToken){
				options.headers['LB_uToken'] = model.uToken;
			}
			if(model.headerLBForceServer){	
				options.headers['LB_ForceServer'] = model.headerLBForceServer;
			}
		}

		if(model.header_apppsk){
			options.headers['apppsk'] = model.header_apppsk;
		}

		Object.keys(params.requestBody).forEach(propName => {
			options.headers[propName] = params.requestBody[propName];
		});
	
		this.#execOrPostpone(options);
	};

	#startRequestRetries(){
		if(this.retrying) return;

		model.dispatchEvent('NetworkError', {
			msg: model.getStr('NetworkConnError').replace('{seconds}', this.constructor.RETRY_COUNT),
			status: 'error',
		});

		var count = this.constructor.RETRY_COUNT;
		this.retrying = true;
		this.firstReqSent = false;

		this.retryInterval = setInterval(() => {
			if(count == 0){				
				model.dispatchEvent('NetworkError', {
					status: 'retry',
				});
				
				this.#haltRetryInterval();
				this.retryTimeout = setTimeout(() => {
					this.#sendFirstReqFromPool();
					this.retrying = false;
					this.firstReqSent = true;
				}, 1000);
			}
			else{
				count--;
				model.dispatchEvent('NetworkError', {
					msg: model.getStr('NetworkConnError').replace('{seconds}', count),
					status: 'error',
				});
			}
		}, 1000);
	}

	#haltRetryInterval(){
		clearInterval(this.retryInterval);
		delete this.retryInterval;
		clearTimeout(this.retryTimeout);
	}

	#sendFirstReqFromPool(){
		if(!this.firstReqSent){
			var opts = this.pool[0];
			opts && this.#sendHTTPReq(opts);
		}
	}

	#execOrPostpone(options){
		if(!document.hidden) this.#sendHTTPReq(options);
		else this.postponePool.push(options);
	}

	async #sendHTTPReq(options){
		if(options.contentType) options.headers['Content-Type'] = options.contentType;

		if(options.accepts) options.headers['Accept'] = options.accepts;
		else options.headers['Accept'] = 'text/plain, */*; q=0.01';

		if(options.global) model.globalRequests++;

		if(options.processData) {
			if(isObject(options.data)){
				options.data = new URLSearchParams(options.data);
			}

			if(!options.contentType) options.headers['Content-Type'] = 'application/x-www-form-urlencoded';
		}

		try{
			model.dispatchEvent('ShowPreloader');

			let reqParams = {
				headers: options.headers,
			};

			if(typeof options.cache === 'boolean' && !options.cache) options.url = addQueryStringValues(options.url, {'_': Date.now()});

			if(typeof options.cache === 'string') 		reqParams.cache = options.cache;
			if(typeof options.type === 'string') 		reqParams.method = options.type;
			if(options.data) 							reqParams.body = options.data;

			let res = await fetch(options.url, reqParams);

			if(!res.ok && typeof options.error === 'function'){
				options.error(res, res.status, res.statusText);
				return;
			}

			let data = null;

			switch(options.dataType){
				case 'json' : 
					data = await res.json();
					break;
				case 'xml' : 
					data = parseXML(await res.text());
					break;
				default: 
					data = await res.text();
					break;
			}

			if(typeof options.success === 'function'){
				options.success(data, res.status, res);
			}
		}
		catch(e){
			if(typeof options.error === 'function') options.error({status: 0}, 'network', e.message);
		}
	}
};